import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { GroupingDefinition, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  LoadContentDocument,
  TrainingsEntitiesContentBlockStatusEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { Block } from 'modules/trainings/lib/content/Block'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

import { contentRegistry } from './ContentRegistry'

type ModuleInfo = Record<string, { title: string; blockCount: number }>

interface SyllabusItem {
  [key: string]: unknown
  moduleReference: string
  block: Block
}

function getCompletionStatus(status: TrainingsEntitiesContentBlockStatusEnum) {
  const config = {
    completed: { status: 'positive', label: i18n.t('trainings.content.syllabus.status.completed') },
    inprogress: { status: 'info', label: i18n.t('trainings.content.syllabus.status.in_progress') },
    notstarted: {
      status: 'neutral',
      label: i18n.t('trainings.content.syllabus.status.not_started'),
    },
  }
  return config[status]
}

export default function Syllabus({
  trainingId,
  employeeId,
}: {
  trainingId: number
  employeeId?: number
}) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const moduleInfoRef = useRef<ModuleInfo>({})

  const grouping: GroupingDefinition<SyllabusItem> = {
    hideSelector: true,
    collapsible: true,
    mandatory: true,
    defaultOpenGroups: true,
    groupBy: {
      moduleReference: {
        name: 'Module', // This is never shown, as it's the selector is hidden
        label: (groupId) => moduleInfoRef.current[String(groupId)]?.title ?? String(groupId),
        itemCount: (groupId) => moduleInfoRef.current[String(groupId)]?.blockCount ?? 0,
      },
    },
  }

  const source = useGraphqlDataCollectionSource({
    document: LoadContentDocument,
    mapData: (data): SyllabusItem[] => {
      const content = graphqlContentMapper.fromGraphQL(
        data.trainings.contentModules,
        Number(trainingId)
      )

      moduleInfoRef.current = Object.fromEntries(
        content.modules.map((module) => [
          module.uuid,
          { title: module.title, blockCount: module.blocks.length },
        ])
      )

      return content.modules.flatMap((module) =>
        module.blocks.map((block) => ({ moduleReference: module.uuid, block }))
      )
    },
    mapPagination: (data) => {
      return { total: data.trainings.contentModules.length, hasNextPage: false } // We're loading the whole thing at once.
    },
    mapVariables: () => ({ trainingId, employeeId }),
    ...(employeeId && {
      itemUrl: ({ block }: { block: Block }) =>
        block.accesible
          ? navigation.myTrainings.revamp.detailsRevamp.content.block.fullPath({
              trainingId,
              blockId: block.id,
            })
          : undefined,
    }),
    paginationType: 'infinite-scroll', // This is a HACK to hide the bottom pagination. Since we load the whole thing at once, it should be fine.
    grouping,
  })

  return (
    <OneDataCollection
      emptyStates={{
        'no-data': {
          emoji: '🧩',
          title: i18n.t('trainings.content.empty.header'),
          description: i18n.t('trainings.content.empty.description'),
          actions: [
            {
              icon: Add,
              label: i18n.t('trainings.content.empty.button'),
              onClick: () => {
                navigate(
                  navigation.trainings.list.details_revamp.content.builder.fullPath({
                    id: trainingId,
                  })
                )
              },
              variant: 'outline',
            },
          ],
        },
      }}
      source={source}
      visualizations={[
        {
          type: 'list',
          options: {
            itemDefinition({ block }) {
              const description = contentRegistry.fetch(block, 'label')?.()
              const icon = contentRegistry.fetch(block, 'icon')

              return {
                title: String(block.title),
                description: description ? [description] : undefined,
                avatar: {
                  type: 'icon',
                  icon,
                  'aria-label': description,
                },
              }
            },
            fields: employeeId
              ? [
                  {
                    label: 'Status',
                    render: ({ block }) => ({
                      type: 'status',
                      value: getCompletionStatus(block.status),
                    }),
                  },
                ]
              : [],
          },
        },
      ]}
    />
  )
}
